import React, { useState, useEffect, useRef } from 'react';
import { apiCall } from '../utils/api';
import { SecureFileLink } from './hris/SecureFileLink';
import { toast } from './ui/sonner';
import { 
  Award, Plus, Upload, Calendar, Building2, FileText, 
  AlertTriangle, CheckCircle, Clock, Trash2, Edit, X, 
  RefreshCw, Eye, Download, Shield, AlertCircle
} from 'lucide-react';

export default function CertificateManagement({ onClose }) {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCert, setEditingCert] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    certificate_name: '',
    issuing_organization: '',
    issue_date: '',
    expiry_date: '',
    certificate_number: '',
    file_url: '',
    file_name: '',
    notes: ''
  });

  useEffect(() => {
    loadCertificates();
  }, []);

  const loadCertificates = async () => {
    setLoading(true);
    try {
      const response = await apiCall('/api/worker/certificates');
      setCertificates(response.certificates || []);
    } catch (error) {
      toast.error('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload PDF, JPEG, or PNG');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/worker/certificates/upload`, {
        method: 'POST',
        // iter77 Phase B: cookie-based auth, no Bearer header needed.
        credentials: 'include',
        body: formDataUpload
      });

      const data = await response.json();
      
      if (data.success) {
        setFormData(prev => ({
          ...prev,
          file_url: data.file_url,
          file_name: data.file_name
        }));
        toast.success('File uploaded successfully');
      } else {
        toast.error(data.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.certificate_name) {
      toast.error('Certificate name is required');
      return;
    }

    try {
      if (editingCert) {
        await apiCall(`/api/worker/certificates/${editingCert._id}`, {
          method: 'PUT',
          body: JSON.stringify(formData)
        });
        toast.success('Certificate updated');
      } else {
        await apiCall('/api/worker/certificates', {
          method: 'POST',
          body: JSON.stringify(formData)
        });
        toast.success('Certificate added');
      }
      
      setShowAddModal(false);
      setEditingCert(null);
      resetForm();
      loadCertificates();
    } catch (error) {
      toast.error(error.message || 'Failed to save certificate');
    }
  };

  const handleEdit = (cert) => {
    setEditingCert(cert);
    setFormData({
      certificate_name: cert.certificate_name || '',
      issuing_organization: cert.issuing_organization || '',
      issue_date: cert.issue_date ? cert.issue_date.split('T')[0] : '',
      expiry_date: cert.expiry_date ? cert.expiry_date.split('T')[0] : '',
      certificate_number: cert.certificate_number || '',
      file_url: cert.file_url || '',
      file_name: cert.file_name || '',
      notes: cert.notes || ''
    });
    setShowAddModal(true);
  };

  const handleDelete = async (certId) => {
    if (!window.confirm('Are you sure you want to delete this certificate?')) return;

    try {
      await apiCall(`/api/worker/certificates/${certId}`, {
        method: 'DELETE'
      });
      toast.success('Certificate deleted');
      loadCertificates();
    } catch (error) {
      toast.error('Failed to delete certificate');
    }
  };

  const resetForm = () => {
    setFormData({
      certificate_name: '',
      issuing_organization: '',
      issue_date: '',
      expiry_date: '',
      certificate_number: '',
      file_url: '',
      file_name: '',
      notes: ''
    });
  };

  const getStatusBadge = (cert) => {
    if (!cert.expiry_date) {
      return (
        <span className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
          <Shield className="w-3 h-3" />
          No Expiry
        </span>
      );
    }
    
    if (cert.status === 'expired') {
      return (
        <span className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">
          <AlertTriangle className="w-3 h-3" />
          Expired
        </span>
      );
    }
    
    if (cert.status === 'expiring_soon') {
      return (
        <span className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs">
          <AlertCircle className="w-3 h-3" />
          Expires in {cert.days_until_expiry} days
        </span>
      );
    }
    
    return (
      <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
        <CheckCircle className="w-3 h-3" />
        Valid
      </span>
    );
  };

  const getVerificationBadge = (status, method) => {
    switch (status) {
      case 'verified':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
            <CheckCircle className="w-3 h-3" />
            Verified {method === 'automatic' ? '(Auto)' : '(Manual)'}
          </span>
        );
      case 'rejected':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">
            <X className="w-3 h-3" />
            Rejected
          </span>
        );
      case 'pending_manual':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs">
            <Clock className="w-3 h-3" />
            Awaiting Manual Review
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
            <Clock className="w-3 h-3" />
            Pending Verification
          </span>
        );
    }
  };

  const [verifying, setVerifying] = useState(null);

  const handleAutoVerify = async (certId) => {
    setVerifying(certId);
    try {
      const response = await apiCall(`/api/worker/certificates/${certId}/auto-verify`, {
        method: 'POST'
      });
      
      if (response.verified === true) {
        toast.success(`Certificate verified via ${response.provider}!`);
      } else if (response.verified === false) {
        toast.error(response.message || 'Certificate verification failed');
      } else {
        toast.info('Automatic verification unavailable. Certificate sent for manual review.');
      }
      
      loadCertificates();
    } catch (error) {
      toast.error(error.message || 'Verification failed');
    } finally {
      setVerifying(null);
    }
  };

  const expiredCount = certificates.filter(c => c.status === 'expired').length;
  const expiringCount = certificates.filter(c => c.status === 'expiring_soon').length;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <Award className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">My Certificates</h2>
                <p className="text-white/80">{certificates.length} certificate{certificates.length !== 1 ? 's' : ''} on file · synced to your HR &amp; Compliance vault</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  resetForm();
                  setEditingCert(null);
                  setShowAddModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2.5 bg-white/20 hover:bg-white/30 rounded-xl transition"
                data-testid="add-certificate-btn"
              >
                <Plus className="w-5 h-5" />
                Add Certificate
              </button>
              <button
                onClick={onClose}
                className="p-2.5 hover:bg-white/20 rounded-xl transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>
          
          {/* Alert badges */}
          {(expiredCount > 0 || expiringCount > 0) && (
            <div className="flex gap-3 mt-4">
              {expiredCount > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-red-500/20 border border-red-300/30 rounded-lg text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  {expiredCount} expired
                </div>
              )}
              {expiringCount > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 border border-amber-300/30 rounded-lg text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {expiringCount} expiring soon
                </div>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[65vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-emerald-600 animate-spin" />
            </div>
          ) : certificates.length === 0 ? (
            <div className="text-center py-12">
              <div className="p-4 bg-emerald-100 rounded-full w-fit mx-auto mb-4">
                <Award className="w-12 h-12 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Certificates Yet</h3>
              <p className="text-gray-600 mb-6">Add your professional certificates to boost your profile</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-medium hover:shadow-lg transition"
              >
                <Plus className="w-5 h-5" />
                Add Your First Certificate
              </button>
            </div>
          ) : (
            <div className="grid gap-4">
              {certificates.map((cert) => (
                <div
                  key={cert._id}
                  className={`p-5 rounded-2xl border transition ${
                    cert.status === 'expired' 
                      ? 'bg-red-50 border-red-200'
                      : cert.status === 'expiring_soon'
                      ? 'bg-amber-50 border-amber-200'
                      : 'bg-white border-gray-200 hover:shadow-md'
                  }`}
                  data-testid={`certificate-${cert._id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-xl ${
                        cert.status === 'expired' 
                          ? 'bg-red-100 text-red-600'
                          : cert.status === 'expiring_soon'
                          ? 'bg-amber-100 text-amber-600'
                          : 'bg-emerald-100 text-emerald-600'
                      }`}>
                        <Award className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">{cert.certificate_name}</h3>
                        
                        {cert.issuing_organization && (
                          <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                            <Building2 className="w-4 h-4" />
                            {cert.issuing_organization}
                          </p>
                        )}
                        
                        <div className="flex flex-wrap gap-2 mt-3">
                          {getStatusBadge(cert)}
                          {getVerificationBadge(cert.verification_status, cert.verification_method)}
                        </div>
                        
                        {/* Verification Notes */}
                        {cert.verification_notes && cert.verification_status !== 'pending' && (
                          <p className="text-xs text-gray-500 mt-2 italic">
                            {cert.verification_notes}
                          </p>
                        )}
                        
                        <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-500">
                          {cert.issue_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              Issued: {new Date(cert.issue_date).toLocaleDateString()}
                            </span>
                          )}
                          {cert.expiry_date && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              Expires: {new Date(cert.expiry_date).toLocaleDateString()}
                            </span>
                          )}
                          {cert.certificate_number && (
                            <span className="flex items-center gap-1">
                              <FileText className="w-4 h-4" />
                              #{cert.certificate_number}
                            </span>
                          )}
                        </div>
                        
                        {cert.file_url && (
                          <SecureFileLink path={cert.file_url} fileLabel={cert.certificate_name}
                            className="inline-flex items-center gap-1 mt-3 text-sm text-blue-600 hover:text-blue-700" testId={`view-my-cert-${cert._id}`}>
                            <Eye className="w-4 h-4" /> View Document ({cert.file_name || 'Certificate'})
                          </SecureFileLink>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-end gap-2">
                      {/* Auto-Verify Button - only show if pending and has certificate number */}
                      {cert.verification_status === 'pending' && cert.certificate_number && (
                        <button
                          onClick={() => handleAutoVerify(cert._id)}
                          disabled={verifying === cert._id}
                          className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition disabled:opacity-50"
                          title="Attempt automatic verification"
                          data-testid={`verify-cert-${cert._id}`}
                        >
                          {verifying === cert._id ? (
                            <>
                              <RefreshCw className="w-4 h-4 animate-spin" />
                              Verifying...
                            </>
                          ) : (
                            <>
                              <Shield className="w-4 h-4" />
                              Verify
                            </>
                          )}
                        </button>
                      )}
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(cert)}
                          className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition"
                          title="Edit"
                          data-testid={`edit-cert-${cert._id}`}
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(cert._id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition"
                          title="Delete"
                          data-testid={`delete-cert-${cert._id}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Certificate Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    {editingCert ? <Edit className="w-6 h-6" /> : <Plus className="w-6 h-6" />}
                  </div>
                  <h3 className="text-xl font-bold">
                    {editingCert ? 'Edit Certificate' : 'Add Certificate'}
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingCert(null);
                    resetForm();
                  }}
                  className="p-2 hover:bg-white/20 rounded-lg transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[60vh] space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Certificate Name *
                </label>
                <input
                  type="text"
                  value={formData.certificate_name}
                  onChange={(e) => setFormData({ ...formData, certificate_name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Registered Nurse (RN)"
                  required
                  data-testid="cert-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Issuing Organization
                </label>
                <input
                  type="text"
                  value={formData.issuing_organization}
                  onChange={(e) => setFormData({ ...formData, issuing_organization: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., American Red Cross"
                  data-testid="cert-org-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Issue Date
                  </label>
                  <input
                    type="date"
                    value={formData.issue_date}
                    onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                    data-testid="cert-issue-date-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiration Date
                  </label>
                  <input
                    type="date"
                    value={formData.expiry_date}
                    onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                    data-testid="cert-expiry-date-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Certificate Number
                </label>
                <input
                  type="text"
                  value={formData.certificate_number}
                  onChange={(e) => setFormData({ ...formData, certificate_number: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., RN-123456"
                  data-testid="cert-number-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Certificate Document
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center">
                  {formData.file_url ? (
                    <div className="flex items-center justify-between bg-green-50 p-3 rounded-lg">
                      <span className="flex items-center gap-2 text-green-700">
                        <FileText className="w-5 h-5" />
                        {formData.file_name || 'File uploaded'}
                      </span>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, file_url: '', file_name: '' })}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="flex items-center gap-2 mx-auto px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition disabled:opacity-50"
                      >
                        {uploading ? (
                          <>
                            <RefreshCw className="w-5 h-5 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-5 h-5" />
                            Choose File
                          </>
                        )}
                      </button>
                      <p className="text-xs text-gray-500 mt-2">PDF, JPEG, or PNG (max 5MB)</p>
                    </>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  placeholder="Any additional notes..."
                  data-testid="cert-notes-input"
                />
              </div>

              <button
                type="submit"
                className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition"
                data-testid="save-certificate-btn"
              >
                {editingCert ? 'Update Certificate' : 'Add Certificate'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
